/**
 * Dashboard API Client - HTTP client for dashboard analytics endpoints
 */

import type {
  DashboardSummary,
  TaskStatusBreakdown,
  PrStatusBreakdown,
} from '@resolve-ai/shared';

import { fetchWithAuth } from './api-client';

/**
 * History data point type
 */
export interface HistoryDataPoint {
  date: string;
  tasksPending: number;
  tasksInProgress: number;
  tasksCompleted: number;
  tasksFailed: number;
  prsOpen: number;
  prsMerged: number;
  prsClosed: number;
}

/**
 * Dashboard API client
 */
export const dashboardApi = {
  /**
   * Get dashboard summary with KPIs
   */
  async getSummary(): Promise<DashboardSummary> {
    const response = await fetchWithAuth<{ summary: DashboardSummary }>('/dashboard/summary');
    return response.summary;
  },

  /**
   * Get task status breakdown
   */
  async getTaskBreakdown(): Promise<TaskStatusBreakdown> {
    const response = await fetchWithAuth<{ breakdown: TaskStatusBreakdown }>(
      '/dashboard/task-breakdown'
    );
    return response.breakdown;
  },

  /**
   * Get PR status breakdown
   */
  async getPrBreakdown(): Promise<PrStatusBreakdown> {
    const response = await fetchWithAuth<{ breakdown: PrStatusBreakdown }>(
      '/dashboard/pr-breakdown'
    );
    return response.breakdown;
  },

  /**
   * Get historical chart data
   */
  async getHistory(period: 'week' | 'month' = 'week'): Promise<HistoryDataPoint[]> {
    const queryParams = new URLSearchParams({ period });
    const response = await fetchWithAuth<{ history: HistoryDataPoint[] }>(
      `/dashboard/history?${queryParams}`
    );
    return response.history;
  },
};
